/**
 * Context-aware Material / Product Picker.
 *
 * Given a component type + optional slot key, resolves the allowed
 * catalogue categories from the central Component Registry (frontend
 * mirror + backend authoritative), then queries the paginated
 * server-side search endpoint (/api/products/search) — never loads the
 * full catalogue into React.
 *
 * Ranking order:
 *   1. Used in This Project (returned by the search API)
 *   2. Favourites (localStorage, scoped by categories)
 *   3. Recently used (localStorage, global — subset re-ranked here)
 *   4. Compatible catalogue results
 *
 * "Search All" escape hatch flips `strict=false` on the backend which
 * drops the category filter so authorised Studio users can pick a
 * legitimate custom material without recompiling the registry.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Star, StarOff, X, Plus, Layers } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { slotsFor, primaryCategoriesFor } from "@/lib/componentRegistry";
import { toast } from "sonner";
import CustomMaterialDialog from "@/components/CustomMaterialDialog";

const FAV_KEY = "luxe.material.favourites";
const RECENT_KEY = "luxe.material.recent";
const RECENT_MAX = 20;

// ── Phase 2A-ii — canonical readiness badge (backend truth) ────────
// Never invented in the UI. The picker only DISPLAYS what the visual
// asset service classifier returned. When no visual asset exists yet
// the picker shows "Thumbnail Only" so Studio understands the gap.
const READINESS_LABEL = {
  thumbnail_only:      "Thumbnail Only",
  ai_reference_ready:  "AI Reference Ready",
  threed_texture_ready:"3D Texture Ready",
  full_pbr_ready:      "Full PBR Ready",
};

const readStore = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; }
};
const writeStore = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ }
};


export default function MaterialPicker({
  componentType,
  slotKey,          // optional — when set, restrict to slot categories
  projectId,        // enables Used-in-Project ranking
  currentId,        // canonical material_id / product_id
  onPick,           // (product) => void — receives the full product row
  onClear,          // optional: clears the assignment
  compact = false,  // Slim rendering used inside the Properties Panel
  placeholder,      // optional filter-input placeholder (Stage C inheritance hints)
}) {
  const [query, setQuery] = useState("");
  const [strict, setStrict] = useState(true); // Search All = false
  const [items, setItems] = useState([]);
  const [used, setUsed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favIds, setFavIds] = useState(readStore(FAV_KEY));
  const [recentIds, setRecentIds] = useState(readStore(RECENT_KEY));
  const [hasMore, setHasMore] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const abortRef = useRef(null);

  // Compute the allowed categories from the registry.
  const allowedCats = useMemo(() => {
    if (slotKey) {
      const slots = slotsFor(componentType);
      return (slots[slotKey]?.allowed_categories) || [];
    }
    return primaryCategoriesFor(componentType);
  }, [componentType, slotKey]);

  const fetchOnce = useCallback(async (q, isStrict, cat, offset = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      params.set("limit", "40");
      params.set("offset", String(offset));
      params.set("strict", isStrict ? "true" : "false");
      if (isStrict && cat) params.set("category", cat);
      if (projectId) params.set("used_in_project_id", projectId);
      const { data } = await api.get(`/products/search?${params.toString()}`);
      return data;
    } catch (e) {
      toast.error(formatErr(e));
      return { items: [], has_more: false, used_in_project: [] };
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Refresh whenever query, strict, or allowedCats change.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Strict mode: run one query per allowed category, merge, dedupe.
      let merged = [];
      let usedIds = [];
      let more = false;
      if (strict && allowedCats.length > 0) {
        for (const cat of allowedCats) {
          const d = await fetchOnce(query, true, cat, 0);
          if (cancelled) return;
          usedIds = d.used_in_project || usedIds;
          for (const it of d.items) {
            if (!merged.find((m) => m.id === it.id)) merged.push(it);
          }
          if (d.has_more) more = true;
        }
      } else {
        const d = await fetchOnce(query, false, null, 0);
        if (cancelled) return;
        merged = d.items;
        usedIds = d.used_in_project || [];
        more = d.has_more;
      }
      setItems(merged);
      setUsed(usedIds);
      setHasMore(more);
    };
    const t = setTimeout(run, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, strict, allowedCats, fetchOnce, reload]);

  const ranked = useMemo(() => {
    const rank = (p) => {
      if (used.includes(p.id))    return 0;
      if (favIds.includes(p.id))  return 1;
      if (recentIds.includes(p.id)) return 2;
      return 3;
    };
    return [...items].sort((a, b) => rank(a) - rank(b));
  }, [items, used, favIds, recentIds]);

  const toggleFav = (id) => {
    setFavIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev].slice(0, 100);
      writeStore(FAV_KEY, next);
      return next;
    });
  };

  const choose = (p) => {
    setRecentIds((prev) => {
      const next = [p.id, ...prev.filter((x) => x !== p.id)].slice(0, RECENT_MAX);
      writeStore(RECENT_KEY, next);
      return next;
    });
    onPick && onPick(p);
  };

  return (
    <div className={compact ? "" : "space-y-2"} data-testid={`material-picker${slotKey ? `-${slotKey}` : ""}`}>
      <div className="flex items-center gap-1 mb-1">
        <button
          type="button"
          data-testid={`btn-new-custom-material${slotKey ? `-${slotKey}` : ""}`}
          onClick={() => setCustomOpen(true)}
          className="px-2 py-1 text-[9px] uppercase tracking-widest border border-border text-muted-foreground hover:border-foreground hover:text-foreground inline-flex items-center gap-1"
          title="Add a Custom Material with a client / supplier reference image">
          <Plus className="w-2.5 h-2.5" strokeWidth={2}/>Custom
        </button>
        {allowedCats.length > 0 && (
          <button
            type="button"
            data-testid={`btn-search-all${slotKey ? `-${slotKey}` : ""}`}
            onClick={() => setStrict((v) => !v)}
            className={`px-2 py-1 text-[9px] uppercase tracking-widest border ${strict ? "border-border text-muted-foreground" : "border-foreground text-foreground bg-muted"}`}
            title={strict ? "Search all catalogue" : "Restrict to compatible"}>
            {strict ? "Scoped" : "All"}
          </button>
        )}
      </div>
      <div>
        <input
          data-testid={`picker-search${slotKey ? `-${slotKey}` : ""}`}
          aria-label={compact ? "Filter materials" : (strict && allowedCats.length ? `Filter ${slotKey || componentType} materials` : "Filter catalogue")}
          className="luxe-input"
          placeholder={placeholder || (compact ? "Filter…" : (strict && allowedCats.length ? `Filter ${slotKey || componentType}…` : "Filter catalogue…"))}
          value={query}
          onChange={(e) => setQuery(e.target.value)}/>
      </div>

      {currentId && (
        <div className="text-[10px] flex items-center justify-between"
             data-testid={`picker-current${slotKey ? `-${slotKey}` : ""}`}>
          <span className="truncate text-muted-foreground">Selected: {currentId}</span>
          {onClear && (
            <button onClick={onClear} className="text-muted-foreground hover:text-red-600" title="Clear">
              <X className="w-3 h-3" strokeWidth={1.5}/>
            </button>
          )}
        </div>
      )}

      <div className="max-h-56 overflow-y-auto border border-border">
        {loading && (
          <div className="p-2 text-[10px] text-muted-foreground">Loading…</div>
        )}
        {!loading && ranked.length === 0 && (
          <div className="p-2 text-[10px] text-muted-foreground">
            {strict && allowedCats.length ? "No compatible materials — try Search All" : "No matches"}
          </div>
        )}
        {ranked.map((p) => {
          const tag = used.includes(p.id) ? "Used" : favIds.includes(p.id) ? "★" : recentIds.includes(p.id) ? "Recent" : null;
          // Prefer the ACTIVE visual asset thumbnail (canonical chain).
          // Fall back to the legacy product image_path so already-imported
          // products remain visible while their visual assets are backfilled.
          const va = p.visual_asset || null;
          const rawPath = (va && va.thumbnail) || p.image_path;
          const thumbSrc = rawPath ? (rawPath.startsWith("http") || rawPath.startsWith("/") ? rawPath : `/api/files/${rawPath}`) : null;
          const readinessKey = va ? va.readiness_level : "thumbnail_only";
          const readinessLabel = READINESS_LABEL[readinessKey] || readinessKey;
          return (
            <div key={p.id}
                 data-testid={`picker-opt-${p.id}`}
                 data-readiness={readinessKey}
                 data-version={va ? va.version : ""}
                 className={`flex items-center gap-2 px-2 py-1.5 text-[11px] border-b border-border last:border-b-0 hover:bg-muted min-h-[44px] cursor-pointer ${currentId === p.id ? "bg-muted" : ""}`}
                 onClick={() => choose(p)}>
              {thumbSrc
                ? <img src={thumbSrc} alt="" className="w-10 h-10 object-cover border border-border shrink-0"
                       onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                : <div className="w-10 h-10 bg-muted border border-border shrink-0 inline-flex items-center justify-center">
                    <Layers className="w-3 h-3 text-muted-foreground" strokeWidth={1.5}/>
                  </div>
              }
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{p.name}</div>
                <div className="text-[9px] text-muted-foreground truncate">
                  {[p.supplier_name, p.finish || p.colour, p.sku, p.category].filter(Boolean).join(" · ")}
                </div>
                <div className="text-[9px] mt-0.5 flex items-center gap-1 flex-wrap">
                  <span className="whitespace-nowrap px-1 py-0.5 border border-border text-muted-foreground"
                        data-testid={`picker-readiness-${p.id}`}
                        title={va ? `Version V${va.version} · ${readinessLabel}` : "No visual asset — thumbnail only"}>
                    {readinessLabel}{va && va.version ? ` · V${va.version}` : ""}
                  </span>
                  {p.is_custom && (
                    <span className="whitespace-nowrap px-1 py-0.5 border border-border text-muted-foreground">Custom</span>
                  )}
                </div>
              </div>
              {tag && <span className="text-[9px] uppercase tracking-widest text-muted-foreground shrink-0">{tag}</span>}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleFav(p.id); }}
                className="shrink-0 text-muted-foreground hover:text-foreground"
                title={favIds.includes(p.id) ? "Unfavourite" : "Favourite"}>
                {favIds.includes(p.id)
                  ? <Star className="w-3 h-3" strokeWidth={1.5} fill="currentColor"/>
                  : <StarOff className="w-3 h-3" strokeWidth={1.5}/>}
              </button>
            </div>
          );
        })}
        {hasMore && !loading && (
          <div className="p-2 text-[10px] text-muted-foreground text-center border-t border-border">
            Refine your search — more results available
          </div>
        )}
      </div>
      <CustomMaterialDialog
        open={customOpen}
        allowedCategories={allowedCats}
        onClose={() => setCustomOpen(false)}
        onCreated={(p) => {
          setCustomOpen(false);
          // Re-run the search so the new material appears in place
          // and Studio can pick it. Also auto-pick + auto-favourite
          // for a smooth workflow.
          setReload((n) => n + 1);
          setTimeout(() => choose(p), 60);
        }}/>
    </div>
  );
}
